// Matériel des pharmacies (Ordre 12): pure helpers shared by the mission card, the pharmacy tab and the « Matériel » page.
// Unit-tested in equipment.test.js. The server decides every right and rule; these only label, format and prepare requests.
import { hasPermission } from "./permissions";

export const EQUIPMENT_VIEW_ALL = "equipment:view_all";
export const EQUIPMENT_MANAGE_ALL = "equipment:manage_all";
// Who reaches the « Matériel » page: the equipment keys, and whoever works on missions (their issues and those of their missions).
export const EQUIPMENT_READ_KEYS = ["missions:view", "missions:view_all", "missions:manage", "missions:manage_all", "missions:execute",
  EQUIPMENT_VIEW_ALL, EQUIPMENT_MANAGE_ALL];
export const EQUIPMENT_ALL_KEYS = [EQUIPMENT_VIEW_ALL, EQUIPMENT_MANAGE_ALL];

export const RESULT_META = {
  functional: { label: "Fonctionne", short: "OK", className: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
  degraded: { label: "Dégradé", short: "Dégradé", className: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-500" },
  out_of_service: { label: "Hors service", short: "HS", className: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
  not_tested: { label: "Non testé", short: "Non testé", className: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400" },
};
export const RESULTS = ["functional", "degraded", "out_of_service", "not_tested"];
export const FAULTY_RESULTS = ["degraded", "out_of_service"];

export const SEVERITY_META = {
  low: { label: "Faible", className: "bg-slate-100 text-slate-700 border-slate-200" },
  medium: { label: "Moyenne", className: "bg-[#E5F0F5] text-[#083346] border-[#C9DFE8]" },
  high: { label: "Élevée", className: "bg-amber-50 text-amber-800 border-amber-200" },
  critical: { label: "Critique", className: "bg-red-50 text-red-700 border-red-200" },
};
export const SEVERITIES = ["low", "medium", "high", "critical"];

export const ISSUE_STATUS_META = {
  open: { label: "Ouverte", className: "bg-[#E5F0F5] text-[#083346] border-[#C9DFE8]" },
  in_progress: { label: "En cours", className: "bg-teal-50 text-teal-800 border-teal-200" },
  resolved: { label: "Résolue", className: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  abandoned: { label: "Abandonnée", className: "bg-slate-50 text-slate-500 border-slate-200" },
};
export const ACTIVE_ISSUE_STATUSES = ["open", "in_progress"];

// Status changes the issue drawer offers from each status, and what each needs (`resolution`: a description; `reason`: a reason).
export const ISSUE_ACTIONS = {
  open: [{ to: "in_progress", label: "Prendre en charge" }, { to: "resolved", label: "Résoudre", resolution: true },
    { to: "abandoned", label: "Abandonner", reason: true }],
  in_progress: [{ to: "resolved", label: "Résoudre", resolution: true }, { to: "abandoned", label: "Abandonner", reason: true },
    { to: "open", label: "Remettre en attente" }],
  resolved: [{ to: "open", label: "Rouvrir", reason: true }],
  abandoned: [{ to: "open", label: "Rouvrir", reason: true }],
};

export const HISTORY_LABELS = {
  issue_opened: "Anomalie ouverte", issue_updated: "Anomalie modifiée", issue_started: "Prise en charge", issue_resolved: "Résolue",
  issue_abandoned: "Abandonnée", issue_reopened: "Rouverte", issue_follow_up_linked: "Tâche de suivi liée",
};

export function canSeeEquipmentPage(user) {
  return !!user?.features?.equipment && hasPermission(user, EQUIPMENT_READ_KEYS);
}

export function canSeeEveryPark(user) {
  return !!user?.features?.equipment && hasPermission(user, EQUIPMENT_ALL_KEYS);
}

// Same pattern as the server (equipment/rules.py): a label followed by a value. The server refuses it anyway; this only warns early.
const SECRET = /\b(mot\s*de\s*passe|mots\s*de\s*passe|mdp|password|passwd|pwd|pass|code\s*pin|pin|cl[ée]s?\s*(wi-?fi|wpa\d?|r[ée]seau|de\s*s[ée]curit[ée])|code\s*wi-?fi|wpa\d?|psk)\s*[:=]\s*\S+/i;

export function looksLikeSecret(text) {
  return !!text && SECRET.test(text);
}

// Parc: in-service assets first, grouped by category label, retired ones last.
export function groupAssets(assets) {
  const groups = new Map();
  for (const a of assets || []) {
    if (a.lifecycle === "retired") continue;
    const key = a.category_label || a.category;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(a);
  }
  return {
    groups: [...groups.entries()].map(([label, items]) => ({ label, items })),
    retired: (assets || []).filter((a) => a.lifecycle === "retired"),
  };
}

// The asset's known condition: its last check, or « jamais contrôlé ».
export function conditionOf(asset) {
  const result = asset?.last_check?.result;
  return result ? { result, ...RESULT_META[result] } : { result: null, label: "Jamais contrôlé", className: "bg-white text-slate-500 border-slate-200", dot: "bg-slate-300" };
}

// Counts shown on the mission card: in service, checked during THIS mission, faulty.
export function parkSummary(assets, checks) {
  const live = (assets || []).filter((a) => a.lifecycle !== "retired");
  const checkedHere = new Set((checks || []).filter((c) => c.state === "active").map((c) => c.asset_id));
  return {
    total: live.length,
    checked: live.filter((a) => checkedHere.has(a.id)).length,
    faulty: live.filter((a) => FAULTY_RESULTS.includes(a.last_check?.result)).length,
  };
}

// The latest active check of an asset during the mission (what « Tester » shows as already done).
export function lastCheckIn(checks, assetId) {
  return (checks || []).filter((c) => c.asset_id === assetId && c.state === "active").sort((a, b) => (a.checked_at < b.checked_at ? 1 : -1))[0] || null;
}

// An issue pre-filled from a faulty check (never opened silently: the person confirms).
export function issueDraftFromCheck(asset, check) {
  return {
    asset_id: asset?.id || "",
    check_id: check?.id || null,
    severity: check?.result === "out_of_service" ? "high" : "medium",
    title: asset ? `${asset.category_label}${check?.result === "out_of_service" ? " hors service" : " dégradé"}` : "",
    symptoms: check?.measures || "",
  };
}

// Issue changes as the PATCH body: only fields that differ (an emptied optional text becomes null).
export function issueChanges(issue, form) {
  const out = {};
  for (const field of ["title", "severity", "symptoms", "impact", "attempted_action", "responsible_user_id", "due_date"]) {
    const next = typeof form[field] === "string" ? form[field].trim() : form[field];
    const before = issue[field] ?? "";
    if ((next ?? "") !== before) out[field] = next === "" ? null : next;
  }
  return out;
}

export function formatDay(day) {
  if (!day) return "";
  const [y, m, d] = day.split("-");
  return `${d}/${m}/${y}`;
}
